import { useEffect, useRef, useState, type ReactNode } from 'react'
import { sugarGremlinCopyright } from '../config/copyright'
import { homepageV3 } from '../config/homepageV3'
import SiteAnnouncement from './SiteAnnouncement'

const desktopHeaderMedia = '(min-width: 1200px)'

function BagIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M8.5 11.5h15l1.6 15H6.9l1.6-15Z" />
      <path d="M12 12V9.2a4 4 0 0 1 8 0V12" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  )
}

export function HomepageV3Header({
  cartCount,
  activeHref = '/',
  cartEnabled = true,
}: {
  cartCount: number
  activeHref?: string
  cartEnabled?: boolean
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const canonicalActiveHref = activeHref === '/support' ? '/contact' : activeHref

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !menuOpen) return
      setMenuOpen(false)
      menuButtonRef.current?.focus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [menuOpen])

  useEffect(() => {
    const desktop = window.matchMedia(desktopHeaderMedia)
    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false)
    }
    desktop.addEventListener('change', closeOnDesktop)
    return () => desktop.removeEventListener('change', closeOnDesktop)
  }, [])

  return (
    <>
      <SiteAnnouncement />
      <header className="sg-v3-header">
      <div className="sg-v3-shell sg-v3-header__inner">
        <button
          ref={menuButtonRef}
          className="sg-v3-menu-toggle"
          type="button"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-controls="sg-v3-primary-navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MenuIcon />
        </button>
        <a className="sg-v3-logo" href="/" aria-label="Sugar Gremlin home">
          <img src="/assets/brand/sg-horizontal-logo-approved.png" alt="Sugar Gremlin" />
        </a>
        <nav
          className={`sg-v3-nav${menuOpen ? ' is-open' : ''}`}
          id="sg-v3-primary-navigation"
          aria-label="Primary navigation"
        >
          {homepageV3.nav.map((item) => (
            <a
              className={item.href === canonicalActiveHref ? 'is-active' : ''}
              href={item.href}
              key={item.label}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </nav>
        {cartEnabled ? (
          <a className="sg-v3-cart" href="/cart" aria-label={`Cart, ${cartCount} ${cartCount === 1 ? 'box' : 'boxes'}`}>
            <BagIcon />
            {cartCount > 0 ? <span>{cartCount}</span> : null}
          </a>
        ) : (
          <span
            className="sg-v3-cart"
            aria-label={`Cart unavailable while ordering is closed, ${cartCount} ${cartCount === 1 ? 'saved box' : 'saved boxes'}`}
            aria-disabled="true"
          >
            <BagIcon />
            {cartCount > 0 ? <span>{cartCount}</span> : null}
          </span>
        )}
      </div>
      </header>
    </>
  )
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return <div className="sg-v3-footer__column"><h2>{title}</h2>{children}</div>
}

export function HomepageV3Footer() {
  const footer = homepageV3.footer
  return (
    <>
    <footer className="sg-v3-footer" id="footer">
      <div className="sg-v3-shell sg-v3-footer__grid">
        <div className="sg-v3-footer__brand">
          <a href="/" aria-label="Sugar Gremlin home">
            <img src="/assets/brand/sg-horizontal-logo-approved.png" alt="Sugar Gremlin" />
          </a>
        </div>
        <FooterColumn title="ORDER">
          {footer.order.map((link) => <a href={link.href} key={link.label}>{link.label}</a>)}
        </FooterColumn>
        <FooterColumn title="EXPLORE">
          {footer.explore.map((link) => <a href={link.href} key={link.label}>{link.label}</a>)}
        </FooterColumn>
        <FooterColumn title="LEGAL">
          <a href="/legal">Legal &amp; Policies</a>
          {footer.legal.map((link) => <a href={link.href} key={link.label}>{link.label}</a>)}
        </FooterColumn>
        <div className="sg-v3-footer__social">
          {footer.social.map((social) => (
            <a href={social.href} aria-label={social.label} target="_blank" rel="noreferrer" key={social.label}>
              <img src={`/assets/icons/social/${social.label.toLowerCase()}.svg`} alt="" aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>
      <div className="sg-v3-shell sg-v3-footer__bottom">
        <span>{sugarGremlinCopyright()}</span>
        <div
          className="sg-v3-footer__payments"
          aria-label="Accepted online payment methods: Visa, Mastercard, Apple Pay, Google Pay, Afterpay and Link"
        >
          <img src="/assets/icons/payment/visa.svg" alt="" aria-hidden="true" />
          <img src="/assets/icons/payment/mastercard.svg" alt="" aria-hidden="true" />
          <img src="/assets/icons/payment/applepay.svg" alt="" aria-hidden="true" />
          <img src="/assets/icons/payment/googlepay.svg" alt="" aria-hidden="true" />
          <span className="sg-v3-footer__payment-name sg-v3-footer__payment-name--afterpay">
            <img src="/assets/icons/payment/afterpay.svg" alt="" aria-hidden="true" />
            <span>Afterpay</span>
          </span>
          <span className="sg-v3-footer__payment-name">Link</span>
        </div>
      </div>
    </footer>
    </>
  )
}
